import Vue from 'vue';
import {mapGetters} from 'vuex';
import {BaseMixin} from '../../../Mixins';
import Component, {mixins} from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {PublicLinks} from '../../../Enums';
import {
  EventService,
  OrderService,
  PrivacyStatementService,
  TermsAndConditionsService,
} from '../../../Services';
import {Event, PrivacyStatement, TermsAndConditions} from '../../../Models';
import {OrderDTO} from '../../../DTOs';
import {DELETE_CART, SET_REGISTRATION_CODES} from '../../../store/commits';
import Modal from '../../Modal/Modal.vue';

@Component({
  computed: {
    ...mapGetters({
      userLanguage: 'getUserLanguage',
      storageEvents: 'getEvents',
    }),
  },
  components: {
    Modal,
  },
})
export default class TicketCodeView extends mixins(BaseMixin) {
  @Prop({type: String, required: true}) inventoryUrl: string | undefined;
  @Prop({type: String, required: true}) loginUrl: string | undefined;
  @Prop({type: String, required: true}) loginPageUrl: string | undefined;
  @Prop({type: String, required: true}) registerTicketUrl: string | undefined;
  @Prop({type: String, required: true}) eventUids: string | undefined;

  storageEvents!: Array<Event>;
  errorCode: string | null = null;
  hasErrors: boolean = false;
  loading: boolean = false;
  codeLoading: boolean = false;
  newUserCode: string = '';
  userLanguage!: string;
  lastUserCode: string = '';
  newUserCodeError: boolean = false;
  events: Array<Event> = [];
  eventSelection: Event | null = null;
  eventService!: EventService;
  orderDTO: OrderDTO = new OrderDTO();
  orderService!: OrderService;

  private privacyStatementService!: PrivacyStatementService;
  private termsAndConditionsService!: TermsAndConditionsService;
  private privacyStatements: any = {};
  private agb: any = {};
  private isFiltered: boolean = false;

  created() {
    this.privacyStatementService = new PrivacyStatementService(this.inventoryUrl);
    this.termsAndConditionsService = new TermsAndConditionsService(this.inventoryUrl);
    this.eventService = new EventService(this.inventoryUrl);
    this.orderService = new OrderService(this.inventoryUrl);
  }

  async mounted() {
    this.loading = true;
    if (typeof this.inventoryUrl !== 'string') {
      this.hasErrors = true;
      return;
    }
    await this.fetchEvents();

    this.checkForCodeInUrl();
    this.getPrivacyStatements();
  }

  async fetchEvents() {
    const filters: any = {ignoreEmpty: 1};
    if (typeof this.eventUids !== 'undefined' && this.eventUids !== '') {
      filters.id = `[${this.eventUids}]`;
    }

    this.events = await this.eventService.listCached<Event>('', filters);
    this.eventSelection = this.events[0] || null;
  }

  async getPrivacyStatements() {
    if (this.eventSelection) {
      const clientIds = this.events.map(e => e.clientId);
      const statements = await this.privacyStatementService.listCached<PrivacyStatement>('', {clientId: `[${clientIds.join(',')}]`});
      const termsAndConditions = await this.termsAndConditionsService.listCached<TermsAndConditions>(
        '',
        {clientId: `[${clientIds.join(',')}]`},
      );
      this.events.forEach(event => {
        const ps = statements.find(p => p.clientId === event.clientId);
        const tac = termsAndConditions.find(p => p.clientId === event.clientId);
        if (ps) {
          Vue.set(this.privacyStatements, `${event.clientId}`, ps);
        }
        if (tac) {
          Vue.set(this.agb, `${event.clientId}`, tac);
        }
      });
    }
  }

  async redeemCode() {
    this.newUserCode = this.newUserCode.toUpperCase();
    this.errorCode = null;
    this.newUserCodeError = false;
    this.codeLoading = true;
    if (!this.orderDTO.codes.includes(this.newUserCode) && this.newUserCode.length > 0) {
      try {
        const codeResult: CodeResult = await this.orderService.redeemUnclassifiedCode(this.newUserCode);
        if (codeResult.success) {
          if (codeResult.code.parentName === 'registration') {
            this.processRegistrationCode(codeResult.code);
          }
        } else {
          if (codeResult.error) {
            this.errorCode = codeResult.error;
          }

          this.lastUserCode = this.newUserCode;
          this.newUserCodeError = true;
        }
      } catch (e) {
        this.newUserCodeError = true;
      } finally {
        this.codeLoading = false;
      }
    } else {
      this.lastUserCode = this.newUserCode;
      this.newUserCodeError = true;
    }
    this.newUserCode = '';
    this.codeLoading = false;
  }

  processRegistrationCode(codeObject: Code) {
    this.$store.commit(DELETE_CART);
    this.$store.commit(SET_REGISTRATION_CODES, [codeObject.code]);
    if (this.registerTicketUrl && this.loginPageUrl) {
      if (codeObject.registrationRequired === 'Ja') {
        window.location.href = `${this.loginPageUrl}?returnUrl=${encodeURIComponent(this.registerTicketUrl)}`;
      } else {
        window.location.href = this.registerTicketUrl;
      }
    }
  }

  openPrivacyModal() {
    // @ts-ignore
    this.$refs.privacyModal.showModal();
  }

  openAGBModal() {
    // @ts-ignore
    this.$refs.agbModal.showModal();
  }

  get selectedPrivacyStatement() {
    if (this.eventSelection) {
      return this.privacyStatements[this.eventSelection.clientId] || null;
    }
    return null;
  }

  get selectedAGB() {
    if (this.eventSelection) {
      return this.agb[this.eventSelection.clientId] || null;
    }
    return null;
  }

  get locales() {
    return `${this.userLanguage}-${this.userLanguage}`;
  }

  get i18nTermsAndConditionsLink(): string {
    return PublicLinks.TERMS_AND_CONDITIONS[this.userLanguage];
  }

  get i18nPrivacyStatementLink(): string {
    return PublicLinks.PRIVACY_STATEMENT[this.userLanguage];
  }

  get i18nPrivacyStatementContent(): string {
    if (this.selectedPrivacyStatement) {
      let content = this.selectedPrivacyStatement.html;
      if (this.userLanguage !== 'de' && this.selectedPrivacyStatement.translations) {
        const translation = this.selectedPrivacyStatement.translations[this.userLanguage];
        content = translation ? translation.html : content;
      }
      return content;
    }
    return '';
  }

  get i18nTermsAndConditionsContent(): string {
    if (this.selectedAGB) {
      let content = this.selectedAGB.html;
      if (this.userLanguage !== 'de' && this.selectedAGB.translations) {
        const translation = this.selectedAGB.translations[this.userLanguage];
        content = translation ? translation.html : content;
      }
      return content;
    }
    return '';
  }

  /* reset error when newUserCode updates */
  @Watch('newUserCode')
  setNewUserCode(newValue: string, oldValue: string) {
    if (oldValue !== newValue && newValue !== '') {
      this.newUserCode = newValue.toUpperCase();
      this.newUserCodeError = false;
    }
  }

  private async checkForCodeInUrl() {
    const urlIdentifier = 'ticket-code=';
    let hash = window.location.hash;
    hash = hash.substring(hash.indexOf('#') + 1);
    if (hash.includes(urlIdentifier)) {
      this.newUserCode = hash.substring(hash.indexOf(urlIdentifier) + urlIdentifier.length);
      this.redeemCode();
    }
  }
}
